import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { getCredentialType } from '../lib/credential-types';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';

import type { CredentialTypeDefinition, CredentialFieldDefinition } from '../lib/credential-types';
import type { CredentialIsolation, SharingMode } from '../lib/types';

export interface CredentialEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    credential_type: string;
    service_name: string;
    access_level: 'read' | 'read_write';
    sharing_mode: SharingMode;
    isolation: CredentialIsolation;
    fields: Record<string, unknown>;
  }) => void;
  /** When editing, pass existing values. When creating, pass the selected type. */
  typeDef: CredentialTypeDefinition;
  initialValues?: {
    name?: string;
    sharing_mode?: SharingMode;
    isolation?: CredentialIsolation;
    fields?: Record<string, unknown>;
    access_level?: 'read' | 'read_write';
  };
  mode: 'create' | 'edit';
  isSaving?: boolean;
}

type EditorTab = 'connection' | 'sharing' | 'details';

const ISOLATION_OPTIONS: { value: CredentialIsolation; label: string; description: string }[] = [
  { value: 'personal', label: 'Personal', description: 'Only you can see and use this credential.' },
  { value: 'tenant', label: 'Team (Tenant-wide)', description: 'All team members can see and use this credential. Requires admin role.' },
];

const SHARING_OPTIONS: { value: SharingMode; label: string; description: string }[] = [
  { value: 'plan_alias', label: 'Vault Alias (Most Secure)', description: 'Agent receives a vault alias. Real credentials resolve at the network boundary.' },
  { value: 'plan_cleartext', label: 'Plan Cleartext', description: 'Agent receives cleartext credentials only during approved plan execution.' },
  { value: 'always_cleartext', label: 'Always Cleartext', description: 'Agent always has cleartext access. Use for non-sensitive services.' },
];

function PasswordField({ field, value, onChange }: { field: CredentialFieldDefinition; value: string; onChange: (v: string) => void }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder ?? ''}
        className="w-full pr-10 px-sd-3 py-sd-2 bg-sd-surface border border-sd-border/30 rounded-sd-md text-sd-sm text-sd-text placeholder:text-sd-text-muted focus:ring-2 focus:ring-sd-primary focus:outline-none transition-colors"
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-sd-text-muted hover:text-sd-text transition-colors"
        aria-label={visible ? 'Hide value' : 'Show value'}
      >
        {visible ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

export function CredentialEditor({ isOpen, onClose, onSave, typeDef, initialValues, mode, isSaving = false }: CredentialEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('connection');
  const [name, setName] = useState(initialValues?.name ?? `${typeDef.label} credential`);
  const [sharingMode, setSharingMode] = useState<SharingMode>(initialValues?.sharing_mode ?? 'plan_alias');
  const [isolation, setIsolation] = useState<CredentialIsolation>(initialValues?.isolation ?? 'personal');
  const [accessLevel, setAccessLevel] = useState<'read' | 'read_write'>(initialValues?.access_level ?? 'read');
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of typeDef.fields) {
      initial[f.key] = (initialValues?.fields?.[f.key] as string) ?? '';
    }
    return initial;
  });

  const handleSave = () => {
    onSave({
      name,
      credential_type: typeDef.slug,
      service_name: typeDef.label.toLowerCase().replace(/\s+/g, '_'),
      access_level: accessLevel,
      sharing_mode: sharingMode,
      isolation,
      fields,
    });
  };

  const tabs: { id: EditorTab; label: string }[] = [
    { id: 'connection', label: 'Connection' },
    { id: 'sharing', label: 'Sharing' },
    { id: 'details', label: 'Details' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${mode === 'create' ? 'Create' : 'Edit'} ${typeDef.label} Credential`}
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose },
        { label: isSaving ? 'Saving...' : 'Save', variant: 'secondary', onClick: handleSave },
      ]}
      className="[&_[data-testid=modal-panel]]:max-w-2xl"
    >
      <div className="flex gap-sd-4 min-h-[300px]">
        {/* Left sidebar tabs */}
        <div className="flex flex-col gap-sd-1 shrink-0 border-r border-sd-border/20 pr-sd-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`text-left px-sd-3 py-sd-2 rounded-sd-md text-sd-sm transition-colors ${activeTab === tab.id ? 'bg-sd-warning/10 text-sd-warning font-medium' : 'text-sd-text-secondary hover:bg-sd-surface-elevated'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 flex flex-col gap-sd-3">
          {activeTab === 'connection' && (
            <>
              <div>
                <label className="block text-sd-sm font-medium text-sd-text mb-sd-1">Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Credential display name" />
              </div>
              {typeDef.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sd-sm font-medium text-sd-text mb-sd-1">
                    {field.label}{field.required && <span className="text-sd-error ml-0.5">*</span>}
                  </label>
                  {field.type === 'password' ? (
                    <PasswordField field={field} value={fields[field.key] ?? ''} onChange={(v) => setFields({ ...fields, [field.key]: v })} />
                  ) : field.type === 'textarea' ? (
                    <Input variant="textarea" value={fields[field.key] ?? ''} onChange={(e) => setFields({ ...fields, [field.key]: e.target.value })} placeholder={field.placeholder} />
                  ) : (
                    <Input
                      type={field.type}
                      value={fields[field.key] ?? ''}
                      onChange={(e) => setFields({ ...fields, [field.key]: e.target.value })}
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              ))}
            </>
          )}

          {activeTab === 'sharing' && (
            <>
              <div>
                <label className="block text-sd-sm font-medium text-sd-text mb-sd-2">Credential Scope</label>
                <div className="flex flex-col gap-sd-2">
                  {ISOLATION_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-sd-2 p-sd-3 rounded-sd-md border cursor-pointer transition-colors ${isolation === opt.value ? 'border-sd-warning bg-sd-warning/5' : 'border-sd-border/30 hover:bg-sd-surface-elevated'}`}
                    >
                      <input
                        type="radio"
                        name="isolation"
                        value={opt.value}
                        checked={isolation === opt.value}
                        onChange={() => setIsolation(opt.value)}
                        className="mt-0.5 accent-[var(--sd-color-warning)]"
                      />
                      <div>
                        <span className="text-sd-sm font-medium text-sd-text">{opt.label}</span>
                        <p className="text-sd-xs text-sd-text-muted mt-0.5">{opt.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sd-sm font-medium text-sd-text mb-sd-2">Agent Sharing Mode</label>
                <div className="flex flex-col gap-sd-2">
                  {SHARING_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-sd-2 p-sd-3 rounded-sd-md border cursor-pointer transition-colors ${sharingMode === opt.value ? 'border-sd-warning bg-sd-warning/5' : 'border-sd-border/30 hover:bg-sd-surface-elevated'}`}
                    >
                      <input
                        type="radio"
                        name="sharing_mode"
                        value={opt.value}
                        checked={sharingMode === opt.value}
                        onChange={() => setSharingMode(opt.value)}
                        className="mt-0.5 accent-[var(--sd-color-warning)]"
                      />
                      <div>
                        <span className="text-sd-sm font-medium text-sd-text">{opt.label}</span>
                        <p className="text-sd-xs text-sd-text-muted mt-0.5">{opt.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sd-sm font-medium text-sd-text mb-sd-1">Access Level</label>
                <select
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value as 'read' | 'read_write')}
                  className="w-full bg-sd-surface border border-sd-border/30 rounded-sd-md px-sd-3 py-sd-2 text-sd-sm text-sd-text focus:ring-2 focus:ring-sd-primary focus:outline-none transition-colors"
                >
                  <option value="read">Read Only</option>
                  <option value="read_write">Read & Write</option>
                </select>
              </div>
            </>
          )}

          {activeTab === 'details' && (
            <div className="flex flex-col gap-sd-2">
              <div className="flex justify-between text-sd-sm">
                <span className="text-sd-text-secondary">Type</span>
                <span className="text-sd-text font-medium">{typeDef.label}</span>
              </div>
              <div className="flex justify-between text-sd-sm">
                <span className="text-sd-text-secondary">Slug</span>
                <span className="text-sd-text font-mono text-sd-xs">{typeDef.slug}</span>
              </div>
              <div className="flex justify-between text-sd-sm">
                <span className="text-sd-text-secondary">Category</span>
                <span className="text-sd-text">{typeDef.category}</span>
              </div>
              <div className="flex justify-between text-sd-sm">
                <span className="text-sd-text-secondary">Fields</span>
                <span className="text-sd-text">{typeDef.fields.length}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
